import { BuildingTypesEnum } from 'dash-common';
import {
  Devider,
  Drawer,
  FilterContext,
  Flex,
  Form,
  FormContext,
  InputPrice,
  ModalContext,
  RawNumberSelect,
  TagSelect,
  Text,
  useCheckIfSubscriptionFeatureActiveBroker,
  useScreenResolution,
} from 'dash-ui';
import camelCase from 'lodash/camelCase';
import { FormEvent, useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { FormFields } from 'containers/properties/AllPropertiesListContainer/components/PropertiesTopBar/components/AdvancedFilterDrawer/constants/constants';
import useValidation from 'containers/properties/AllPropertiesListContainer/components/PropertiesTopBar/components/AdvancedFilterDrawer/hooks/useValidation';

import {
  YearBuildRangeInput,
  // NumberOfUnitsRawSelectInput,
  FeatureAndAmenitiesSelectInput,
  CreationDateSelect,
} from './components';
// import CompletionDateSelect from './components/CompletionDate/CompletionDateSelect';

const AdvancedFilterDrawer = ({
  isOpen,
  onRequestClose,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['forms', 'pages', 'common']);
  const { state, setFilterState } = useContext(FilterContext);
  const {
    formState,
    formOnSubmit,
    resetForm,
    resetFormErrors,
    stateHandlers,
    setFormValues,
  } = useContext(FormContext);
  const valid = useValidation();
  const formRef = useRef<HTMLFormElement | null>(null);
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const {
    devices: { isMobile },
  } = useScreenResolution();

  const handleUnits =
    (key: string) =>
    (value: number | { min?: number; max?: number } | null) => {
      stateHandlers[FormFields.Units]({ ...formState.units, [key]: value });
    };
  const { toggleModal, setPayload } = useContext(ModalContext);

  useEffect(() => {
    if (isOpen) {
      setFormValues({
        buildingTypes: state.buildingTypes,
        units: state.units,
        numberOfUnit: state.numberOfUnit,
        radius: state.radius,
        location: state.location,
        minPrice: state.prices?.min,
        maxPrice: state.prices?.max,
        amenityIds: state.amenityIds,
        scheduleConstructionStartDate: state.constructionDateRange?.startDate,
        scheduleConstructionEndDate: state.constructionDateRange?.endDate,
        projectStatuses: state.projectStatuses,
        yearBuildMin: state.yearBuildRange?.min,
        yearBuildMax: state.yearBuildRange?.max,
        createdAt: state.createdAt,
        completionDate: state.completionDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, isOpen]);

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!valid(FormFields.NumberOfUnit, formState.numberOfUnit)) {
      return null;
    }
    setFilterState('units', formState?.units || state.units);
    setFilterState(
      'numberOfUnit',
      formState?.numberOfUnit || state.numberOfUnit
    );
    setFilterState(
      'buildingTypes',
      formState?.buildingTypes || state.buildingTypes
    );
    setFilterState('radius', formState?.radius);
    setFilterState('location', formState?.location);
    setFilterState('center', {
      lat: formState?.location?.lat,
      lng: formState?.location?.lng,
    });
    setFilterState('prices', {
      min: formState.minPrice,
      max: formState.maxPrice,
    });
    setFilterState('amenityIds', formState?.amenityIds);
    setFilterState('constructionDateRange', {
      startDate: formState?.scheduleConstructionStartDate,
      endDate: formState?.scheduleConstructionEndDate,
    });
    setFilterState('projectStatuses', formState?.projectStatuses);
    setFilterState('yearBuildRange', {
      min: formState.yearBuildMin,
      max: formState.yearBuildMax,
    });
    setFilterState('createdAt', formState.createdAt);
    setFilterState('completionDate', formState.completionDate);
    resetForm();
    resetFormErrors();
    formOnSubmit();
    onRequestClose();
  };

  const onOpenAddAlertModal = useCallback(() => {
    toggleModal(true, 'AddAlertModal');
    setPayload({
      advancedFilterDrawerData: {
        place: formState.location,
        radius: formState.radius,
        minPropertyPrice: formState.minPrice || state.prices?.min,
        maxPropertyPrice: formState.maxPrice || state.prices?.max,
        buildingTypes: formState?.buildingTypes,
        numberOfBedrooms: formState?.units?.unitNumberOfBedrooms,
        numberOfBathrooms: formState?.units?.unitNumberOfBathrooms,
        featuresAndAmenities: formState?.amenityIds,
        constructionDate: {
          startDate: formState?.scheduleConstructionStartDate,
          endDate: formState?.scheduleConstructionEndDate,
        },
        numberOfUnits: formState?.numberOfUnits,
        projectStatus: formState?.projectStatuses,
        yearBuilt: {
          min: formState.yearBuildMin,
          max: formState.yearBuildMax,
        },
        cratedAt: formState.createdAt,
      },
    });
    onRequestClose();
  }, [toggleModal, setPayload, formState, state, onRequestClose]);

  return (
    <Drawer
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('pages:propertyPage.filterTitle') || ''}
      subtitle={t('pages:propertyPage.filterSubtitle') || ''}
      submitButton={{
        title: t('common:labels.save'),
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('pages:propertyPage.saveAndCreateAlert'),
        disabled: !isSubscriptionActive,
        onClick: onOpenAddAlertModal,
      }}
    >
      <Form mt={6} ref={formRef} onSubmit={onSubmitForm}>
        {isMobile && (
          <Flex alignItems="center" mb={6}>
            <Form.Group>
              <Form.Label>{t('forms:labels.priceMin')}</Form.Label>
              <InputPrice
                w="100%"
                placeholder="$500,000"
                value={formState.minPrice}
                onChangeValue={(value) =>
                  stateHandlers[FormFields.MinPrice](value)
                }
              />
            </Form.Group>
            <Devider mt={5} mx={2.5} w={17} color={theme.colors.n80} />
            <Form.Group>
              <Form.Label>{t('forms:labels.priceMax')}</Form.Label>
              <InputPrice
                w="100%"
                placeholder="$1,000,000"
                value={formState.maxPrice}
                onChangeValue={(value) =>
                  stateHandlers[FormFields.MaxPrice](value)
                }
              />
            </Form.Group>
          </Flex>
        )}
        <Text>{t('pages:propertyPage.propertyType')}</Text>
        <TagSelect
          mb={6}
          mt={2}
          options={Object.values(BuildingTypesEnum).map((item) => ({
            label: t(`forms:labels.buildingTypes.${camelCase(item)}`),
            value: item,
          }))}
          defaultValue={formState?.buildingTypes}
          onChange={(value) => stateHandlers[FormFields.BuildingTypes](value)}
        />
        <Form.Group mb={6}>
          <Form.Label md>{t('pages:propertyPage.numberOfBedrooms')}</Form.Label>
          <RawNumberSelect
            onChange={handleUnits('unitNumberOfBedrooms')}
            value={formState?.units?.unitNumberOfBedrooms}
          />
        </Form.Group>
        <Form.Group mb={5}>
          <Form.Label md>
            {t('pages:propertyPage.numberOfBathrooms')}
          </Form.Label>
          <RawNumberSelect
            onChange={handleUnits('unitNumberOfBathrooms')}
            value={formState?.units?.unitNumberOfBathrooms}
          />
        </Form.Group>

        {/* TODO: Uncomment if needed. Hidden for now */}
        {/* <NumberOfUnitsRawSelectInput /> */}

        <FeatureAndAmenitiesSelectInput
          amenityIds={formState?.amenityIds}
          setAmenityIds={(amenityIds) =>
            stateHandlers[FormFields.AmenityIds](amenityIds)
          }
        />

        <CreationDateSelect
          value={formState.createdAt}
          setCreatedAt={(value) => stateHandlers[FormFields.CreatedAt](value)}
        />

        <Flex alignItems="center" flexDirection={isMobile ? 'column' : 'row'}>
          {/* Hidden by request */}
          {/* <CompletionDateSelect /> */}
          <YearBuildRangeInput
            startYear={formState?.yearBuildMin}
            endYear={formState?.yearBuildMax}
            setYearRange={({ startYear, endYear }) => {
              stateHandlers[FormFields.YearBuildMin](startYear);
              stateHandlers[FormFields.YearBuildMax](endYear);
            }}
          />
        </Flex>
      </Form>
    </Drawer>
  );
};

export default AdvancedFilterDrawer;
